import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import useGetConnectedAccount from 'hooks/query/connected-accounts/use-get-connected-account';
import useCreateConnectedAccount from 'hooks/query/connected-accounts/use-create-connected-account';
import useUpdateConnectedAccount from 'hooks/query/connected-accounts/use-update-connected-account';
import useStripeConnect from 'hooks/query/connected-accounts/use-stripe-connect';
import { ConnectComponentsProvider, ConnectAccountOnboarding, ConnectNotificationBanner } from '@stripe/react-connect-js';
import { StripeConnectInstance } from '@stripe/connect-js';
import { ConnectedAccountDto } from 'common/types/services/api/payment/connected-account-dto.type';

interface StripeOnboardingContextType {
  stripeConnectInstance: StripeConnectInstance | null;
  connectedAccount: ConnectedAccountDto | undefined;
  connectedAccountId: string | null;
  isConnectedAccountOnboarded: boolean;
  isGetConnectedAccountPending: boolean;
  isConnectedAccountPending: boolean;
  handleCreateConnectedAccount: () => Promise<void>;
  handleOnboardingExit: () => Promise<void>;
  refetchConnectedAccount: () => Promise<{ data: ConnectedAccountDto | undefined }>;
  refreshStripeInstance: () => void;
  OnboardingComponent: React.FC<{ onLoaderStart?: () => void }>;
  NotificationBannerComponent: React.FC<{ onLoaderStart?: () => void }>;
}

const StripeOnboardingContext = createContext<StripeOnboardingContextType | undefined>(undefined);

export const StripeOnboardingProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [connectedAccountId, setConnectedAccountId] = useState<string | null>(null);
  const [isConnectedAccountOnboarded, setIsConnectedAccountOnboarded] = useState(false);
  const [stripeRefreshTrigger, setStripeRefreshTrigger] = useState(0);

  const { data: connectedAccount, isPending: isGetConnectedAccountPending, refetch: refetchConnectedAccount } = useGetConnectedAccount();
  const { mutateAsync: createConnectedAccount, isPending: isConnectedAccountPending } = useCreateConnectedAccount();
  const { mutateAsync: updateConnectedAccount } = useUpdateConnectedAccount();
  const stripeConnectInstance = useStripeConnect(connectedAccountId ?? '', stripeRefreshTrigger);

  useEffect(() => {
    if (connectedAccount) {
      setConnectedAccountId(connectedAccount.id);
      setIsConnectedAccountOnboarded(connectedAccount.isOnboarded);
    }
  }, [connectedAccount]);

  const handleCreateConnectedAccount = async () => {
    const newConnectedAccountId = await createConnectedAccount();
    setConnectedAccountId(newConnectedAccountId);
  };

  const handleOnboardingExit = async () => {
    await updateConnectedAccount();
    setIsConnectedAccountOnboarded(true);
    setStripeRefreshTrigger(prev => prev + 1);
  };

  const refreshStripeInstance = () => {
    setStripeRefreshTrigger(prev => prev + 1);
  };

  const OnboardingComponent = ({ onLoaderStart }: { onLoaderStart?: () => void }) => {
    if (!stripeConnectInstance) return null;
    return (
      <ConnectComponentsProvider connectInstance={stripeConnectInstance}>
        <ConnectAccountOnboarding onExit={handleOnboardingExit} onLoaderStart={onLoaderStart} />
      </ConnectComponentsProvider>
    );
  };

  const NotificationBannerComponent = ({ onLoaderStart }: { onLoaderStart?: () => void }) => {
    if (!stripeConnectInstance) return null;
    return (
      <ConnectComponentsProvider connectInstance={stripeConnectInstance}>
        <ConnectNotificationBanner onLoaderStart={onLoaderStart} />
      </ConnectComponentsProvider>
    );
  };
  
  const value = {
    stripeConnectInstance,
    connectedAccount,
    connectedAccountId,
    isConnectedAccountOnboarded,
    isGetConnectedAccountPending,
    isConnectedAccountPending,
    handleCreateConnectedAccount,
    handleOnboardingExit,
    refetchConnectedAccount,
    refreshStripeInstance,
    OnboardingComponent,
    NotificationBannerComponent,
  };

  return <StripeOnboardingContext.Provider value={value}>{children}</StripeOnboardingContext.Provider>;
};

export const useStripeOnboarding = () => {
  const context = useContext(StripeOnboardingContext);
  if (context === undefined) {
    throw new Error('useStripeOnboarding must be used within a StripeOnboardingProvider');
  }
  return context;
};
